import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export const fallbackImage =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGFsaWdubWVudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSIjNjY2NjY2Ij5JbWFnZW4gbm8gZGlzcG9uaWJsZTwvdGV4dD48L3N2Zz4=';

export const getImageSrc = (img) => {
  if (!img) return fallbackImage;
  if (typeof img === 'string') return img;
  if (typeof img === 'object') return img.url || img.src || fallbackImage;
  return fallbackImage;
};

export const handleImgError = (e) => {
  if (!e?.target) return;
  if (e.target.src === fallbackImage) return;
  e.target.src = fallbackImage;
};

const CarCard = ({ car }) => {
  const navigate = useNavigate();

  const handleVerClick = (placeId) => {
    navigate(`/ver/${placeId}`);
  };

  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return '';
  };

  // thumbnailSrc: aceptar array o la primera imagen del objeto car
  const thumbnailSrc =
    // si car.images es array de strings/objetos
    (Array.isArray(car?.images) && getImageSrc(car.images[0])) ||
    // si car.image es string u objeto
    getImageSrc(car?.image);

  const isSold = car?.sold || car?.vendido;

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'transform .2s, box-shadow .2s, border-color .2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 28px rgba(29, 27, 58, 0.10)',
          borderColor: 'transparent',
        },
      }}
    >
      <CardActionArea
        onClick={() => handleVerClick(car?.id)}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            image={thumbnailSrc}
            alt={car?.title || 'Imagen vehículo'}
            onError={handleImgError}
            sx={{ aspectRatio: '4 / 3', objectFit: 'cover', bgcolor: 'background.default' }}
          />
          {isSold && (
            <Chip
              label="Vendido"
              color="secondary"
              size="small"
              sx={{ position: 'absolute', top: 12, left: 12 }}
            />
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {[car?.marca, car?.modelo].filter(Boolean).join(' · ')}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {car?.title}
          </Typography>
          <Typography variant="h6" sx={{ color: 'secondary.dark', fontWeight: 800 }}>
            $ {car?.price}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ color: 'text.secondary', mt: 'auto', pt: 1 }}>
            {car?.km && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <SpeedOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">{car.km} km</Typography>
              </Stack>
            )}
            {car?.city && (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <PlaceOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" noWrap>{car.city}</Typography>
              </Stack>
            )}
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5, mt: 1 }}
          >
            <Typography variant="caption" color="text.secondary">
              {formatDate(car.createdAt)}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'primary.main', fontWeight: 700 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Ver más</Typography>
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default CarCard;
